// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { AxiosResponse } from 'axios';

import type {
  SessionEventListResponse,
  ThreadHistoryResponse,
  ThreadRuntimeHealthResponse,
  ThreadTaskListResponse,
} from '@/types';

import { ApiBase, type ApiRequestConfig } from './apiBase';

const THREAD_HISTORY_CACHE_TTL_MS = 1000;
const THREAD_SESSION_EVENTS_CACHE_TTL_MS = 500;

type CachedResponse<T> = {
  inFlight?: Promise<AxiosResponse<T>>;
  response?: AxiosResponse<T>;
  updatedAt: number;
};

const threadHistoryCache = new Map<string, CachedResponse<ThreadHistoryResponse>>();
const threadSessionEventsCache = new Map<string, CachedResponse<SessionEventListResponse>>();

// N-2: bounded FIFO eviction. The session-events cache keys on serialized
// request params, so without a cap an long session grows the Map unboundedly.
const REQUEST_CACHE_MAX_ENTRIES = 50;

function setRequestCacheEntry<T>(
  cache: Map<string, CachedResponse<T>>,
  key: string,
  value: CachedResponse<T>
) {
  cache.set(key, value);
  while (cache.size > REQUEST_CACHE_MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) return;
    // Never evict the entry we just wrote.
    if (oldestKey === key) return;
    cache.delete(oldestKey);
  }
}

function getNow() {
  return Date.now();
}

export class ApiChat extends ApiBase {
  async getThreadHistory(threadId: string, options?: { bypassCache?: boolean }) {
    // D-005b（对抗审计 P1-2）：stale 探针需要权威真值，可绕开 1s TTL 客户端
    // 缓存——mid-run 调用方可能在挂起落地前留下 pending_interrupts=[] 的缓存，
    // 命中会把新鲜中断卡误判过时。
    const bypassCache = Boolean(options?.bypassCache);
    const key = String(threadId || '').trim();
    if (!key) {
      return this.client.get<ThreadHistoryResponse>(`/v1/chat/threads/${threadId}`, {
        suppressErrorToast: true,
        suppressUnauthorizedRedirect: true,
      } as ApiRequestConfig);
    }

    const cached = threadHistoryCache.get(key);
    const now = getNow();
    if (!bypassCache) {
      if (cached?.inFlight) {
        return cached.inFlight;
      }
      if (cached?.response && now - cached.updatedAt < THREAD_HISTORY_CACHE_TTL_MS) {
        return cached.response;
      }
    }

    const request = this.client
      .get<ThreadHistoryResponse>(`/v1/chat/threads/${threadId}`, {
        suppressErrorToast: true,
        suppressUnauthorizedRedirect: true,
      } as ApiRequestConfig)
      .then((response) => {
        if (!bypassCache) {
          setRequestCacheEntry(threadHistoryCache, key, {
            response,
            updatedAt: getNow(),
          });
        }
        return response;
      })
      .catch((error) => {
        const previous = threadHistoryCache.get(key);
        threadHistoryCache.delete(key);
        // LANE2-P2-B（对抗审计）：bypass 探针是 authoritative read——429 时
        // 回吐可能先于挂起落地的旧缓存属 stale-while-error 反模式，必须让
        // 错误上抛（探针侧 fail-safe=不过时）。
        if (!bypassCache && previous?.response) {
          const status = (error as { response?: { status?: unknown } })?.response?.status;
          if (status === 429) {
            return previous.response;
          }
        }
        throw error;
      });

    if (!bypassCache) {
      setRequestCacheEntry(threadHistoryCache, key, {
        ...cached,
        inFlight: request,
        updatedAt: cached?.updatedAt ?? now,
      });
    }

    return request;
  }

  async getThreadCheckpoints(threadId: string) {
    return this.client.get(`/v1/chat/threads/${threadId}/checkpoints`, {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async getThreadSessionEvents(
    threadId: string,
    params?: {
      limit?: number;
      offset?: number;
      event_type?: string;
    },
    options?: { forceRefresh?: boolean }
  ): Promise<AxiosResponse<SessionEventListResponse>> {
    const key = JSON.stringify({
      threadId: String(threadId || '').trim(),
      params: params ?? {},
    });
    const forceRefresh = options?.forceRefresh === true;
    const cached = threadSessionEventsCache.get(key);
    const now = getNow();
    // A forced refresh still joins an in-flight request: it is already newer
    // than the TTL window, and a second concurrent 200-event query only
    // burns the gateway rate-limit budget.
    if (cached?.inFlight) {
      return cached.inFlight;
    }
    if (
      !forceRefresh &&
      cached?.response &&
      now - cached.updatedAt < THREAD_SESSION_EVENTS_CACHE_TTL_MS
    ) {
      return cached.response;
    }

    const request = this.client
      .get<SessionEventListResponse>(`/v1/chat/threads/${threadId}/session-events`, {
        params,
        suppressErrorToast: true,
        suppressUnauthorizedRedirect: true,
      } as ApiRequestConfig)
      .then((response) => {
        setRequestCacheEntry(threadSessionEventsCache, key, {
          response,
          updatedAt: getNow(),
        });
        return response;
      })
      .catch((error) => {
        const previous = threadSessionEventsCache.get(key);
        threadSessionEventsCache.delete(key);
        if (previous?.response) {
          const status = (error as { response?: { status?: unknown } })?.response?.status;
          if (status === 429) {
            return previous.response;
          }
        }
        throw error;
      });

    setRequestCacheEntry(threadSessionEventsCache, key, {
      ...cached,
      inFlight: request,
      updatedAt: cached?.updatedAt ?? now,
    });
    return request;
  }

  async getThreadTasks(
    threadId: string,
    params?: {
      limit?: number;
    }
  ): Promise<AxiosResponse<ThreadTaskListResponse>> {
    return this.client.get<ThreadTaskListResponse>(`/v1/chat/threads/${threadId}/tasks`, {
      params,
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  /** 任务级重试（R1-3）：failed manim 任务重入队。 */
  async retryThreadTask(
    threadId: string,
    taskId: string
  ): Promise<AxiosResponse<{ accepted: boolean; task_id: string; job_id: string; retry_count?: number }>> {
    return this.client.post(
      `/v1/chat/threads/${threadId}/tasks/${taskId}/retry`,
      {},
      { suppressErrorToast: false, suppressUnauthorizedRedirect: true } as ApiRequestConfig
    );
  }

  async getThreadRuntimeHealth(
    threadId: string
  ): Promise<AxiosResponse<ThreadRuntimeHealthResponse>> {
    return this.client.get<ThreadRuntimeHealthResponse>(
      `/v1/chat/threads/${threadId}/runtime-health`,
      { suppressErrorToast: true, suppressUnauthorizedRedirect: true } as ApiRequestConfig
    );
  }

  async getChatCapabilities() {
    // 登录竞态防护：capabilities 是能力预检，非会话端点。401（token 尚未
    // 就绪/能力未配置）时不得触发全局登出（handleUnauthorized 清 token），
    // 否则登录后主聊天挂载会被误登出。调用方按空能力降级。
    // 可观测性：抑制登出不等同于静默——401 仍记录诊断日志，区分登录竞态
    // 与真实凭据失效（后者表现为登录稳定后持续 401）。
    try {
      return await this.client.get('/v1/chat/capabilities', {
        suppressUnauthorizedRedirect: true,
        suppressErrorToast: true,
      } as ApiRequestConfig);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        console.warn(
          '[capabilities] 401 suppressed (login race or missing credentials); ' +
            'capabilities fall back to defaults',
        );
      }
      throw error;
    }
  }

  async replayFromCheckpoint(data: {
    thread_id: string;
    checkpoint_id: string;
    replay_mode?: 'fork';
    fork_thread_id?: string;
  }) {
    return this.client.post('/v1/chat/replay', data);
  }

  // SSE stream 已退役，使用 CopilotKit AG-UI runtime
  async resumeThread(data: {
    thread_id: string;
    resume_value: unknown;
    interrupt_id?: string;
  }) {
    return this.client.post('/v1/chat/resume', data);
  }

  async sendUiAction(data: {
    thread_id: string;
    message_id: string;
    block_id: string;
    action: string;
    params?: Record<string, unknown>;
  }) {
    return this.client.post('/v1/chat/ui-action', data);
  }

  async sendUiBlockRenderFail(data: {
    thread_id?: string;
    message_id?: string;
    block_id?: string;
    block_type?: string;
  }) {
    return this.client.post('/v1/chat/ui-block-render-fail', data);
  }

  async sendMessageFeedback(data: {
    thread_id: string;
    message_id: string;
    feedback: 'up' | 'down';
  }) {
    return this.client.post('/v1/chat/feedback', data);
  }

  async reportCopilotRuntimeError(data: {
    thread_id?: string;
    agent_id?: string;
    code?: string;
    message: string;
    context?: Record<string, unknown>;
  }) {
    return this.client.post('/v1/chat/copilotkit/error', data, {
      suppressErrorToast: true,
    } as ApiRequestConfig);
  }

  async getCopilotPhysicsStatus() {
    return this.client.get('/v1/copilotkit/physics');
  }

  /**
   * Persist a DeepAgent bridge result into the main-chat thread history.
   *
   * When an inline DeepAgent completes, its final result summary is projected
   * as a single assistant message into the SOURCE thread (not the DeepAgent
   * thread) so it survives refresh / session switching. The backend writes it
   * into the checkpointer's messages channel via `aupdate_state`, which makes
   * it appear in `GET /threads/{source_thread_id}` history. Failures are
   * non-fatal — the frontend injects the message locally first, so this call
   * only guarantees cross-refresh survival.
   */
  async persistDeepAgentBridgeResult(data: {
    thread_id: string;
    content: string;
    active_agent: string;
    handoff_id?: string;
    deepagent_thread_id?: string;
    debrief?: {
      conclusion: string;
      key_points: string[];
      assumptions?: string[];
      next_steps: string[];
    };
  }) {
    return this.client.post('/v1/chat/deepagent-bridge/result', data, {
      suppressErrorToast: true,
    } as ApiRequestConfig);
  }

  static __clearRequestCachesForTests() {
    threadHistoryCache.clear();
    threadSessionEventsCache.clear();
  }
}
